// ── Enums / Literal Types ─────────────────────────────────────────────────────

export type ConfidentialityLevel = "PUBLIC" | "INTERNAL" | "CONFIDENTIAL" | "SECRET";

export type DocumentStatus =
  | "DRAFT"
  | "DIAJUKAN"
  | "DITINJAU"
  | "DISETUJUI"
  | "DITERBITKAN"
  | "DITOLAK"
  | "DIARSIPKAN";

export type DocumentAction =
  | "VIEW"
  | "DOWNLOAD"
  | "EDIT"
  | "DELETE"
  | "APPROVE"
  | "REJECT"
  | "UPLOAD"
  | "CREATE";

// ── Master Reference Types ────────────────────────────────────────────────────

export interface JenisPNBP {
  id: string;
  kode: string;       // J01–J06
  nama: string;
  deskripsi?: string;
  urutan: number;
}

export interface SiklusKeuangan {
  id: string;
  kode: string;       // S01–S04
  nama: string;
  deskripsi?: string;
  urutan: number;
}

export interface BentukDokumen {
  id: string;
  kode: string;       // B01–B05
  nama: string;
  deskripsi?: string;
  urutan: number;
  jenis_repositori?: "LEGAL" | "NON_LEGAL" | null;
}

export interface Dimensi {
  id: string;
  kode: string;       // D01–D08
  nama: string;
  deskripsi?: string;
  urutan: number;
}

export interface Tag {
  id: string;
  nama: string;
  kategori?: string;
  warna?: string;
}

export interface StatusDokumen {
  id: string;
  kode: DocumentStatus;
  nama: string;
}

export interface KategoriInformasi {
  id: string;
  kode: string;
  nama: string;
  deskripsi?: string;
  urutan: number;
}

export interface SubKategori {
  id: string;
  kategori_informasi_id: string;
  nama: string;
  deskripsi?: string;
  urutan: number;
}

// ── Approval History ─────────────────────────────────────────────────────────

export interface ApprovalHistoryItem {
  id: string;
  dokumen_id: string;
  user_id: string;
  username: string;
  full_name?: string;
  aksi: string;
  status_asal: string;
  status_tujuan: string;
  catatan?: string;
  created_at: string;
}

export const AKSI_LABELS: Record<string, string> = {
  SUBMIT: "Mengajukan dokumen",
  REVIEW_APPROVE: "Lolos tinjauan (Manager)",
  REVIEW_REJECT: "Ditolak pada tinjauan (Manager)",
  APPROVE_APPROVE: "Disetujui (Leader)",
  APPROVE_REJECT: "Ditolak pada persetujuan (Leader)",
  APPROVE: "Disetujui (Leader)",
  REJECT: "Ditolak pada persetujuan (Leader)",
  PUBLISH: "Diterbitkan",
};

export const AKSI_COLORS: Record<string, string> = {
  SUBMIT: "text-blue-600",
  REVIEW_APPROVE: "text-blue-700",
  REVIEW_REJECT: "text-red-600",
  APPROVE_APPROVE: "text-emerald-700",
  APPROVE_REJECT: "text-red-600",
  APPROVE: "text-emerald-700",
  REJECT: "text-red-600",
  PUBLISH: "text-green-700",
};

// ── Document Types ────────────────────────────────────────────────────────────

export interface DocumentVersion {
  id: string;
  versi_ke: number;
  file_name: string;
  file_size: number;
  file_hash: string;
  catatan?: string;
  uploaded_at: string;
  uploaded_by_username?: string;
}

export interface Document {
  id: string;
  judul: string;
  deskripsi?: string;
  nomor_dokumen?: string;

  // Classification
  jenis_pnbp_id: string;
  jenis_pnbp_kode: string;
  jenis_pnbp_nama: string;
  siklus_keuangan_id: string;
  siklus_keuangan_kode: string;
  siklus_keuangan_nama: string;
  bentuk_dokumen_id: string;
  bentuk_dokumen_kode: string;
  bentuk_dokumen_nama: string;
  /** "LEGAL" → UU/PP/PMK/Permen/Perdirjen/SOP/Berita Acara; "NON_LEGAL" → Faktur/Bukti Bayar/Laporan/Kajian/Audit/Histories. */
  bentuk_dokumen_jenis_repositori?: "LEGAL" | "NON_LEGAL" | null;

  // Status & access
  status_kode: DocumentStatus;
  status_nama: string;
  tingkat_kerahasiaan: ConfidentialityLevel;

  // File info
  file_name?: string;
  file_size?: number;
  mime_type?: string;

  // Dates
  tanggal_dokumen?: string;
  tanggal_berlaku?: string;
  tanggal_berakhir?: string;

  // Relations
  dimensi: Array<{ id: string; kode: string; nama: string }>;
  tags: Array<{ id: string; nama: string; kategori?: string | null }>;
  pic: Array<{ id: string; nama: string; jabatan?: string }>;
  stakeholders: Array<{ id: string; nama: string; tipe?: string }>;
  unit_kerja: Array<{ id: string; kode: string; nama: string }>;

  // Stats
  total_views: number;
  total_watchers: number;
  is_watched: boolean;

  // Knowledge taxonomy
  kategori_informasi_id?: string;
  kategori_informasi_nama?: string;
  sub_kategori_id?: string;
  sub_kategori_nama?: string;
  taxonomy_node_id?: string;
  taxonomy_node_nama?: string;
  taxonomy_node_path?: string;
  /** Names from root to leaf: ["PNBP", "PNBP SDA", "PNBP Mineral dan Batubara", ...] */
  taxonomy_breadcrumb?: string[];
  /** Semua node taxonomy terpilih (untuk pre-fill picker di edit / tampil di detail) */
  taxonomy_nodes?: {
    id: string;
    label: string;
    is_primary: boolean;
    kodes: string[];
  }[];

  // Audit
  created_by_user_id: string;
  created_by_username: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentListResponse {
  items: Document[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Form / Upload Types ───────────────────────────────────────────────────────

export interface DocumentUpload {
  judul: string;
  deskripsi?: string;
  nomor_dokumen?: string;
  jenis_pnbp_id: string;
  siklus_keuangan_id: string;
  bentuk_dokumen_id: string;
  tingkat_kerahasiaan: ConfidentialityLevel;
  dimensi_ids: string[];
  tag_ids: string[];
  free_tags: string[];
  pic_ids: string[];
  stakeholder_ids: string[];
  unit_kerja_ids: string[];
  tanggal_dokumen?: string;
  tanggal_berlaku?: string;
  tanggal_berakhir?: string;
  periode_retensi_tahun?: number;
  kategori_informasi_id?: string;
  sub_kategori_id?: string;
  file?: File;
}

// ── Document Tree Types ──────────────────────────────────────────────────────

export interface DocumentTreeFile {
  id: string;
  judul: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  status_kode: DocumentStatus;
  status_nama: string;
  tingkat_kerahasiaan: ConfidentialityLevel;
  created_by_username: string;
  created_at: string;
  versi_terakhir: number;
}

export interface DocumentTreeBentuk {
  kode: string;
  nama: string;
  documents: DocumentTreeFile[];
  total: number;
}

export interface DocumentTreeSiklus {
  kode: string;
  nama: string;
  bentuk_list: DocumentTreeBentuk[];
  total: number;
}

export interface DocumentTreeJenis {
  kode: string;
  nama: string;
  siklus_list: DocumentTreeSiklus[];
  total: number;
}

export interface DocumentTreeResponse {
  tree: DocumentTreeJenis[];
  total_documents: number;
  total_jenis: number;
  total_siklus: number;
  total_bentuk: number;
}

// ── Upload Response Types ────────────────────────────────────────────────────

export interface StorageTreeNode {
  kode: string;
  nama: string;
  tipe: "root" | "jenis_pnbp" | "siklus_keuangan" | "bentuk_dokumen" | "file";
}

export interface DocumentUploadResponse {
  document_id: string;
  judul: string;
  file_name: string;
  file_size: number;
  file_hash: string;
  mime_type: string;
  versi_ke: number;
  message: string;
  jenis_pnbp_kode: string;
  jenis_pnbp_nama: string;
  siklus_keuangan_kode: string;
  siklus_keuangan_nama: string;
  bentuk_dokumen_kode: string;
  bentuk_dokumen_nama: string;
  storage_tree: StorageTreeNode[];
}

// ── Search Types ──────────────────────────────────────────────────────────────

export interface SearchFilters {
  jenis_pnbp_ids?: string[];
  siklus_ids?: string[];
  bentuk_ids?: string[];
  dimensi_ids?: string[];
  tag_ids?: string[];
  free_tags?: string[];
  status_ids?: string[];
  confidentiality?: ConfidentialityLevel[];
  date_range?: {
    start?: string;
    end?: string;
  };
  expiry_range?: {
    start?: string;
    end?: string;
  };
}

export interface SearchRequest {
  keyword?: string;
  filters: SearchFilters;
  page: number;
  page_size: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface SearchResultItem {
  id: string;
  judul: string;
  nomor_dokumen?: string;
  jenis_pnbp_kode: string;
  jenis_pnbp_nama: string;
  siklus_keuangan_nama: string;
  bentuk_dokumen_nama: string;
  status_kode: DocumentStatus;
  status_nama: string;
  tingkat_kerahasiaan: ConfidentialityLevel;
  tanggal_dokumen?: string;
  tanggal_berakhir?: string;
  created_by_username: string;
  created_at: string;
  rank?: number;
}

export interface SearchResponse {
  items: SearchResultItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  keyword?: string;
  took_ms?: number;
}

// ── Confidentiality badge helpers ─────────────────────────────────────────────

export const CONFIDENTIALITY_LABELS: Record<ConfidentialityLevel, string> = {
  PUBLIC: "Publik",
  INTERNAL: "Internal",
  CONFIDENTIAL: "Rahasia",
  SECRET: "Sangat Rahasia",
};

export const CONFIDENTIALITY_COLORS: Record<ConfidentialityLevel, string> = {
  PUBLIC: "bg-green-100 text-green-800",
  INTERNAL: "bg-blue-100 text-blue-800",
  CONFIDENTIAL: "bg-yellow-100 text-yellow-800",
  SECRET: "bg-red-100 text-red-800",
};

export const STATUS_COLORS: Record<DocumentStatus, string> = {
  DRAFT: "bg-gray-100 text-gray-700",
  DIAJUKAN: "bg-yellow-100 text-yellow-800",
  DITINJAU: "bg-blue-100 text-blue-800",
  DISETUJUI: "bg-emerald-100 text-emerald-800",
  DITERBITKAN: "bg-green-100 text-green-800",
  DITOLAK: "bg-red-100 text-red-800",
  DIARSIPKAN: "bg-purple-100 text-purple-800",
};
